import { Mesh, type PartDefinition, type VirtualMeshUserData } from '@rogue/rackbuilder'
import { MassStorageOptions } from '../../RackTypes'
import { MMS_VIRTUAL_MESH_CONFIG, type VirtualMeshDimensionConfig } from './mms-virtual-mesh-config'

const VM_DEBUG = typeof window !== 'undefined' && window.location.search.includes('virtual_mesh_debug')

type VirtualMeshCounter = { value: number }

type Object3D = any;
type MeshStandardMaterial = any;

export function addVirtualMeshesToUnt(
  part: PartDefinition,
  visited: Set<PartDefinition> = new Set(),
  config: MassStorageOptions,
  counterRef: VirtualMeshCounter = { value: 0 },
  productConfig: VirtualMeshDimensionConfig = MMS_VIRTUAL_MESH_CONFIG
): void {
  // Prevent infinite recursion
  if (visited.has(part)) return
  visited.add(part)

  // Check all slots for shelf_two instances
  Object.entries(part.slots).forEach(([slotName, slotContent]) => {
    if (slotName.includes('shelf_two_') || slotName.includes('left_side_corner') || slotName.includes('right_side_corner')) {
      const shelves = Array.isArray(slotContent) ? slotContent : [slotContent]
      shelves.forEach((shelf) => {
        if (shelf && typeof shelf === 'object' && 'mesh' in shelf) {
          const shelfPart = shelf as PartDefinition
          const uniqueId = `${counterRef.value += 1}`

          // Create a state-based virtual mesh component
          const createVirtualMeshComponent = () => {
            let isHovered = false
            let isRecentlyClicked = false
            let clickTimeout: ReturnType<typeof setTimeout> | null = null

            const updateVisualization = (mesh: Mesh, engine?: any) => {
              const material = mesh.material as MeshStandardMaterial
              if (!material) return

              material.needsUpdate = true
              if (engine) {
                engine.render()
              }
            }

            return {
              onHover: (mesh: Object3D, event: PointerEvent) => {
                isHovered = true
                updateVisualization(mesh as Mesh)
              },

              onUnhover: (mesh: Object3D, event: PointerEvent) => {
                isHovered = false
                updateVisualization(mesh as Mesh)
              },

              onClick: (mesh: Object3D, event: PointerEvent) => {
                const virtualMesh = mesh as Mesh
                const userData = virtualMesh.userData as VirtualMeshUserData
                const engine = userData?.builder?.getEngine()

                if (counterRef.value > 1) {
                  const uniqueIdNum = parseInt(uniqueId, 10)
                  const isRightSide = config.rightAddonChain.length > 0
                  const activeAddonChain = isRightSide ? config.rightAddonChain : config.leftAddonChain

                  let actualAccordionIndex: number

                  if (isRightSide) {
                    if (uniqueIdNum === counterRef.value) {
                      // Last mesh (highest ID) is the base unit
                      actualAccordionIndex = config.baseAccordionIndex // This should be 0 for base
                    } else {
                      // For addons, map directly to the accordion index from the chain
                      const addonIndex = uniqueIdNum - 1 // Convert 1-based ID to 0-based index
                      actualAccordionIndex = activeAddonChain[addonIndex]?.accordionIndex ?? config.baseAccordionIndex
                    }
                  } else {
                    // Left side logic remains the same
                    actualAccordionIndex = uniqueIdNum === 1
                      ? config.baseAccordionIndex
                      : activeAddonChain[uniqueIdNum - 2]?.accordionIndex ?? config.baseAccordionIndex
                  }

                  config.onVirtualMeshClick?.(actualAccordionIndex)
                }

                // Focus camera on the virtual mesh
                if (engine) {
                  const virtualMeshManager = engine.inputManager.getVirtualMeshManager()
                  virtualMeshManager.focusOnVirtualMesh(uniqueId, 2.0, {
                    updateTarget : true,
                    updateZoom   : true,
                    skipAnimate  : false,
                  })
                }

                // Set clicked state
                isRecentlyClicked = true
                updateVisualization(virtualMesh, engine)

                // Clear clicked state after timeout
                if (clickTimeout) {
                  clearTimeout(clickTimeout)
                }
                clickTimeout = setTimeout(() => {
                  isRecentlyClicked = false
                  updateVisualization(virtualMesh, engine)
                }, 200)
              },
            }
          }

          const meshComponent = createVirtualMeshComponent()

          const isShelf   = slotName.includes('shelf_two_')
          const isCorner  = slotName.includes('left_side_corner') || slotName.includes('right_side_corner')
          const isRightCorner = slotName.includes('right_side_corner')
          const slotKeys  = Object.keys(part.slots)
          const is70Wide  = shelfPart.mesh.includes('70')

          // Resolve corner dimensions from config
          let cornerDimensions = productConfig.corner.h43.dimensions
          let cornerOffsetX    = productConfig.corner.h43.offsetX
          let cornerOffsetY    = productConfig.corner.h43.offsetY
          let cornerOffsetZ    = productConfig.corner.h43.offsetZ

          if (isCorner && productConfig.corner.h100 && slotKeys.some(name => name.includes('100'))) {
            cornerDimensions = productConfig.corner.h100.dimensions
            cornerOffsetX = productConfig.corner.h100.offsetX
            cornerOffsetY = productConfig.corner.h100.offsetY
            cornerOffsetZ = productConfig.corner.h100.offsetZ
          } else if (isCorner && slotKeys.some(name => name.includes('70'))) {
            cornerDimensions = productConfig.corner.h70.dimensions
            cornerOffsetX = productConfig.corner.h70.offsetX
            cornerOffsetY = productConfig.corner.h70.offsetY
            cornerOffsetZ = productConfig.corner.h70.offsetZ
          }

          const cornerOffset = isRightCorner
            ? { x: -cornerOffsetX, y: cornerOffsetY, z: cornerOffsetZ }
            : { x: cornerOffsetX, y: cornerOffsetY, z: cornerOffsetZ }

          // Resolve shelf dimensions from config
          const widthKey  = is70Wide ? 'w70' : 'w43'
          let shelfSpec   = productConfig.shelf[widthKey].h43

          if (isShelf && productConfig.shelf[widthKey].h100 && slotKeys.some(name => name.includes('100'))) {
            shelfSpec = productConfig.shelf[widthKey].h100
          } else if (isShelf && slotKeys.some(name => name.includes('70'))) {
            shelfSpec = productConfig.shelf[widthKey].h70
          }

          shelfPart.virtualAttachments = {
            main: [{
              id         : uniqueId,
              dimensions : isCorner ? cornerDimensions : shelfSpec.dimensions,
              offset     : isCorner ? cornerOffset : shelfSpec.offset,
              color      : '#00ff00',
              opacity    : VM_DEBUG ? 0.35 : 0,
              onHover    : meshComponent.onHover,
              onUnhover  : meshComponent.onUnhover,
              onClick    : meshComponent.onClick,
            }],
          }
        }
      })
    }

    // Recursively check nested parts
    if (Array.isArray(slotContent)) {
      slotContent.forEach((item) => {
        if (item && typeof item === 'object' && 'slots' in item) {
          addVirtualMeshesToUnt(item as PartDefinition, visited, config, counterRef, productConfig)
        }
      })
    } else if (slotContent && typeof slotContent === 'object' && 'slots' in slotContent) {
      addVirtualMeshesToUnt(slotContent as PartDefinition, visited, config, counterRef, productConfig)
    }
  })
}
